import './notification-card.scss';
import {useState, useEffect, useContext} from 'react';
import {NotificationType} from '../../backend/enums/notification-type';
import {
  AffiliationInviteNotificationData,
  CaregiverAdministratorRoleRequestResultNotificationData,
  DrawingSentNotificationData,
  DrawingSharedNotificationData,
  MediaEmptyNotificationData,
  MediaProcessingStatusNotificationData,
  MissedCallNotificationData,
  Notification,
  OccurrenceAssignedNotificationData,
  OccurrenceNotificationData,
  PersonalDataSharingPreferencesChangeNotificationData,
  PhotoSentNotificationData,
  PhotoSharedNotificationData,
  RelationshipEndNotificationData,
  RelationshipInviteNotificationData,
  RelationshipInviteRequestResultNotificationData,
  RelationshipRoleChangeNotificationData,
  ReminderResponseNotificationData,
  SosRequestNotificationData,
  UserSignedInNotificationData,
  VideoMessageNotificationData,
  VideoSentNotificationData,
  VideoSharedNotificationData,
  WithoutLicenseNotificationData,
} from '../../backend/interfaces/notification';
import {Box, Avatar, Typography, Badge, useTheme, Button, Radio, Switch} from '@mui/material';
import {translations} from '../../i18n/translations';
import {useTranslation, Trans} from 'react-i18next';
import {ReminderResponseValue} from '../../backend/enums/reminder-response-value';
import {AuthContextType} from '../../contexts/auth/types';
import {AuthContext, useAuth} from '../../contexts/auth/context';
import {NotificationsRepository} from '../../repositories/notifications';
import {OfficeContextType} from '../../contexts/office/types';
import {OfficeContext} from '../../contexts/office/context';
import {InviteIdentificationType} from '../../enums/invite-identification-type';
import {MediaProcessType} from '../../backend/enums/media-process-type';
import {ProcessStatus} from '../../backend/enums/process-status';
import {InviteType} from '../../backend/enums/invite-type';
import {getBrandName} from '../../utils/functions/get-product-name';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import EmailIcon from '@mui/icons-material/Email';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import WarningIcon from '@mui/icons-material/Warning';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import VideocamIcon from '@mui/icons-material/Videocam';
import AlarmIcon from '@mui/icons-material/Alarm';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import LensIcon from '@mui/icons-material/Lens';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import moment from 'moment';
import {EntityPendingTask} from '../../backend/interfaces/entity-pending-task';
import {CareTask} from '../../backend/interfaces/care-task';
import {MdOutlineTipsAndUpdates} from 'react-icons/md';
import {ScreenPaths} from '../../enums';
import {useHistory, useLocation} from 'react-router-dom';
import {Tooltip} from '../tooltip/tooltip';
import {WarningRounded} from '@mui/icons-material';

interface NotificationCardData {
  isValidNotification: boolean;
  message: string;
  icon: React.ReactElement;
  wasRead: boolean;
  avatarName: string;
  avatarPicture?: string;
  invite?: {
    id: string;
    type: InviteType;
  };
  imageUrl?: string;
  videoUrl?: string;
  personalDataSharingPreferences?: {
    canAccessProfileData: boolean;
    canAccessContacts: boolean;
    canAccessApplications: boolean;
    canAccessMultimedia: boolean;
    canAccessReminders: boolean;
    canAccessActivityFeed: boolean;
  };
  date?: Date;
  occurrenceId?: string;
  occurrenceEntityId?: string;
  occurrenceRegisteredAt?: string;
}

/**
 * Notification card used on the notifications screen.
 */
export const NotificationCard = (props: {notification: Notification<any>}) => {
  const authContext: AuthContextType = useContext(AuthContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const appContext: AppContextType = useContext(AppContext);
  const defaultNotificationCardData: NotificationCardData = {
    isValidNotification: false,
    message: '',
    avatarName: '',
    icon: <></>,
    wasRead: false,
  };
  const [notificationCardData, setNotificationCardData] = useState<NotificationCardData>(
    defaultNotificationCardData
  );
  const [errorOnImage, setErrorOnImage] = useState<boolean>(false);
  const [errorOnVideo, setErrorOnVideo] = useState<boolean>(false);
  const {t} = useTranslation();
  const theme = useTheme();
  const history = useHistory();

  // Set notification data
  useEffect(() => {
    switch (props.notification.type) {
      case NotificationType.UserSignedIn:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.user_signed_in, {
            name:
              (props.notification as Notification<UserSignedInNotificationData>).data?.userName ||
              '',
            brand: getBrandName(appContext.state.configuration.product),
          }),
          icon: <LoginIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<UserSignedInNotificationData>).data?.userName || '',
          avatarPicture: (props.notification as Notification<UserSignedInNotificationData>).data
            ?.userPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<UserSignedInNotificationData>).createdAt,
        });
        break;
      case NotificationType.WithoutLicense:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.without_license, {
            name:
              (props.notification as Notification<WithoutLicenseNotificationData>).data?.userName ||
              '',
            brand: getBrandName(appContext.state.configuration.product),
          }),
          icon: <WarningIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<WithoutLicenseNotificationData>).data?.userName ||
            '',
          avatarPicture: (props.notification as Notification<WithoutLicenseNotificationData>).data
            ?.userPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<WithoutLicenseNotificationData>).createdAt,
        });
        break;
      case NotificationType.MissedCall:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.missed_call, {
            name:
              (props.notification as Notification<MissedCallNotificationData>).data?.callerName ||
              '',
          }),
          icon: <PhoneMissedIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<MissedCallNotificationData>).data?.callerName || '',
          avatarPicture: (props.notification as Notification<MissedCallNotificationData>).data
            ?.callerPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<MissedCallNotificationData>).createdAt,
        });
        break;
      case NotificationType.PhotoSent:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.photo_sent, {
            name:
              (props.notification as Notification<PhotoSentNotificationData>).data?.fromUserName ||
              '',
          }),
          icon: <PhotoCameraIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<PhotoSentNotificationData>).data?.fromUserName ||
            '',
          avatarPicture: (props.notification as Notification<PhotoSentNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          imageUrl: (props.notification as Notification<PhotoSentNotificationData>).data?.mediaUrl,
          date: (props.notification as Notification<PhotoSentNotificationData>).createdAt,
        });
        break;
      case NotificationType.PhotoShared:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.photo_shared, {
            name:
              (props.notification as Notification<PhotoSharedNotificationData>).data
                ?.fromUserName || '',
          }),
          icon: <PhotoCameraIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<PhotoSharedNotificationData>).data?.fromUserName ||
            '',
          avatarPicture: (props.notification as Notification<PhotoSharedNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          imageUrl: (props.notification as Notification<PhotoSharedNotificationData>).data
            ?.mediaUrl,
          date: (props.notification as Notification<PhotoSharedNotificationData>).createdAt,
        });
        break;
      case NotificationType.SosRequest:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.sos_request, {
            name:
              (props.notification as Notification<SosRequestNotificationData>).data
                ?.seniorUserName || '',
          }),
          icon: <WarningIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<SosRequestNotificationData>).data?.seniorUserName ||
            '',
          avatarPicture: (props.notification as Notification<SosRequestNotificationData>).data
            ?.seniorUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<SosRequestNotificationData>).createdAt,
        });
        break;
      case NotificationType.VideoMessage:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.video_message, {
            name:
              (props.notification as Notification<VideoMessageNotificationData>).data
                ?.fromUserName || '',
          }),
          icon: <VideocamIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<VideoMessageNotificationData>).data?.fromUserName ||
            '',
          avatarPicture: (props.notification as Notification<VideoMessageNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          videoUrl: (props.notification as Notification<VideoMessageNotificationData>).data
            ?.mediaUrl,
          date: (props.notification as Notification<VideoMessageNotificationData>).createdAt,
        });
        break;
      case NotificationType.VideoSent:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.video_sent, {
            name:
              (props.notification as Notification<VideoSentNotificationData>).data?.fromUserName ||
              '',
          }),
          icon: <VideocamIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<VideoSentNotificationData>).data?.fromUserName ||
            '',
          avatarPicture: (props.notification as Notification<VideoSentNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          videoUrl: (props.notification as Notification<VideoSentNotificationData>).data?.mediaUrl,
          date: (props.notification as Notification<VideoSentNotificationData>).createdAt,
        });
        break;
      case NotificationType.VideoShared:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.video_shared, {
            name:
              (props.notification as Notification<VideoSharedNotificationData>).data
                ?.fromUserName || '',
          }),
          icon: <VideocamIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<VideoSharedNotificationData>).data?.fromUserName ||
            '',
          avatarPicture: (props.notification as Notification<VideoSharedNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          videoUrl: (props.notification as Notification<VideoSharedNotificationData>).data
            ?.mediaUrl,
          date: (props.notification as Notification<VideoSharedNotificationData>).createdAt,
        });
        break;
      case NotificationType.DrawingSent:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.drawing_sent, {
            name:
              (props.notification as Notification<DrawingSentNotificationData>).data
                ?.fromUserName || '',
          }),
          icon: <ModeEditIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<DrawingSentNotificationData>).data?.fromUserName ||
            '',
          avatarPicture: (props.notification as Notification<DrawingSentNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          imageUrl: (props.notification as Notification<DrawingSentNotificationData>).data
            ?.mediaUrl,
          date: (props.notification as Notification<DrawingSentNotificationData>).createdAt,
        });
        break;
      case NotificationType.DrawingShared:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.drawing_shared, {
            name:
              (props.notification as Notification<DrawingSharedNotificationData>).data
                ?.fromUserName || '',
          }),
          icon: <ModeEditIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<DrawingSharedNotificationData>).data
              ?.fromUserName || '',
          avatarPicture: (props.notification as Notification<DrawingSharedNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          imageUrl: (props.notification as Notification<DrawingSharedNotificationData>).data
            ?.mediaUrl,
          date: (props.notification as Notification<DrawingSharedNotificationData>).createdAt,
        });
        break;
      case NotificationType.RelationshipInvite:
        setNotificationCardData({
          isValidNotification: true,
          message: getRelationshipInviteMessage(
            (props.notification as Notification<RelationshipInviteNotificationData>).data
          ),
          icon: <EmailIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<RelationshipInviteNotificationData>).data
              ?.targetUserName || '',
          avatarPicture: (props.notification as Notification<RelationshipInviteNotificationData>)
            .data?.targetUserPicture,
          invite: (props.notification as Notification<RelationshipInviteNotificationData>).data
            ?.inviteId
            ? {
                id:
                  (props.notification as Notification<RelationshipInviteNotificationData>).data
                    ?.inviteId || '',
                type: InviteType.Relationship,
              }
            : undefined,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<RelationshipInviteNotificationData>).createdAt,
        });
        break;
      case NotificationType.AffiliationInvite:
        setNotificationCardData({
          isValidNotification: true,
          message: getAffiliationInviteMessage(
            (props.notification as Notification<AffiliationInviteNotificationData>).data
          ),
          icon: <EmailIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<AffiliationInviteNotificationData>).data
              ?.targetEntityName || '',
          avatarPicture: (props.notification as Notification<AffiliationInviteNotificationData>)
            .data?.targetEntityPicture,
          invite: (props.notification as Notification<AffiliationInviteNotificationData>).data
            ?.inviteId
            ? {
                id:
                  (props.notification as Notification<AffiliationInviteNotificationData>).data
                    ?.inviteId || '',
                type: InviteType.Affiliation,
              }
            : undefined,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<RelationshipInviteNotificationData>).createdAt,
        });
        break;
      case NotificationType.ReminderResponse:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.reminder_response.message, {
            name:
              (props.notification as Notification<ReminderResponseNotificationData>).data
                ?.responseByUserName || '',
            reminder: (props.notification as Notification<ReminderResponseNotificationData>).data
              ?.reminderTitle,
            response: getReminderResponse(
              (props.notification as Notification<ReminderResponseNotificationData>).data?.value
            ),
          }),
          icon: <AlarmIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<ReminderResponseNotificationData>).data
              ?.responseByUserName || '',
          avatarPicture: (props.notification as Notification<ReminderResponseNotificationData>).data
            ?.responseByUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<ReminderResponseNotificationData>).createdAt,
        });
        break;
      case NotificationType.RelationshipInviteRequestResult:
        setNotificationCardData({
          isValidNotification: true,
          message:
            (props.notification as Notification<RelationshipInviteRequestResultNotificationData>)
              .data?.requestAccepted === true
              ? t(
                  translations.notification_card.relationship_invite_request_result
                    .message_accepted,
                  {
                    name:
                      (
                        props.notification as Notification<RelationshipInviteRequestResultNotificationData>
                      ).data?.evaluatedByUserName || '',
                    target:
                      (
                        props.notification as Notification<RelationshipInviteRequestResultNotificationData>
                      ).data?.toBeInvitedUserName || '',
                  }
                )
              : t(
                  translations.notification_card.relationship_invite_request_result
                    .message_declined,
                  {
                    name:
                      (
                        props.notification as Notification<RelationshipInviteRequestResultNotificationData>
                      ).data?.evaluatedByUserName || '',
                    target:
                      (
                        props.notification as Notification<RelationshipInviteRequestResultNotificationData>
                      ).data?.toBeInvitedUserName || '',
                  }
                ),
          icon:
            (props.notification as Notification<RelationshipInviteRequestResultNotificationData>)
              .data?.requestAccepted === true ? (
              <DoneIcon className="notification-card-avatar-badge-icon" />
            ) : (
              <CloseIcon className="notification-card-avatar-badge-icon" />
            ),
          avatarName:
            (props.notification as Notification<RelationshipInviteRequestResultNotificationData>)
              .data?.evaluatedByUserName || '',
          avatarPicture: (
            props.notification as Notification<RelationshipInviteRequestResultNotificationData>
          ).data?.evaluatedByUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (
            props.notification as Notification<RelationshipInviteRequestResultNotificationData>
          ).createdAt,
        });
        break;
      case NotificationType.CaregiverAdministratorRoleRequestResult:
        setNotificationCardData({
          isValidNotification: true,
          message:
            (
              props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
            ).data?.requestAccepted === true
              ? t(
                  translations.notification_card.caregiver_administrator_role_request_result
                    .message_accepted,
                  {
                    name:
                      (
                        props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
                      ).data?.evaluatedByUserName || '',
                    target:
                      (
                        props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
                      ).data?.toBeCaregiverAdministratorName || '',
                  }
                )
              : t(
                  translations.notification_card.caregiver_administrator_role_request_result
                    .message_declined,
                  {
                    name:
                      (
                        props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
                      ).data?.evaluatedByUserName || '',
                    target:
                      (
                        props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
                      ).data?.toBeCaregiverAdministratorName || '',
                  }
                ),
          icon:
            (
              props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
            ).data?.requestAccepted === true ? (
              <DoneIcon className="notification-card-avatar-badge-icon" />
            ) : (
              <CloseIcon className="notification-card-avatar-badge-icon" />
            ),
          avatarName:
            (
              props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
            ).data?.evaluatedByUserName || '',
          avatarPicture: (
            props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
          ).data?.evaluatedByUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (
            props.notification as Notification<CaregiverAdministratorRoleRequestResultNotificationData>
          ).createdAt,
        });
        break;
      case NotificationType.RelationshipRoleChange:
        setNotificationCardData({
          isValidNotification: true,
          message: getRelationshipRoleChangeMessage(
            (props.notification as Notification<RelationshipRoleChangeNotificationData>).data
          ),
          icon: <PublishedWithChangesIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<RelationshipRoleChangeNotificationData>).data
              ?.changeMadeByUserName || '',
          avatarPicture: (
            props.notification as Notification<RelationshipRoleChangeNotificationData>
          ).data?.changeMadeByUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<RelationshipRoleChangeNotificationData>)
            .createdAt,
        });
        break;
      case NotificationType.PersonalDataSharingPreferencesChange:
        setNotificationCardData({
          isValidNotification: true,
          message: t(
            translations.notification_card.personal_data_sharing_preferences_change.message,
            {
              name:
                (
                  props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
                ).data?.changeMadeByUserName || '',
              brand: getBrandName(appContext.state.configuration.product),
            }
          ),
          icon: <LockIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (
              props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
            ).data?.changeMadeByUserName || '',
          avatarPicture: (
            props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
          ).data?.changeMadeByUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          personalDataSharingPreferences: {
            canAccessActivityFeed:
              (
                props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
              ).data?.canAccessActivityFeed === true
                ? true
                : false,
            canAccessApplications:
              (
                props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
              ).data?.canAccessApplications === true
                ? true
                : false,
            canAccessContacts:
              (
                props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
              ).data?.canAccessContacts === true
                ? true
                : false,
            canAccessMultimedia:
              (
                props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
              ).data?.canAccessMultimedia === true
                ? true
                : false,
            canAccessProfileData:
              (
                props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
              ).data?.canAccessProfileData === true
                ? true
                : false,
            canAccessReminders:
              (
                props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
              ).data?.canAccessReminders === true
                ? true
                : false,
          },
          date: (
            props.notification as Notification<PersonalDataSharingPreferencesChangeNotificationData>
          ).createdAt,
        });
        break;
      case NotificationType.RelationshipEnd:
        setNotificationCardData({
          isValidNotification: true,
          message: getRelationshipEndMessage(
            (props.notification as Notification<RelationshipEndNotificationData>).data
          ),
          icon: <PersonRemoveIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<RelationshipEndNotificationData>).data
              ?.endedByUserName || '',
          avatarPicture: (props.notification as Notification<RelationshipEndNotificationData>).data
            ?.endedByUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<RelationshipEndNotificationData>).createdAt,
        });
        break;
      case NotificationType.MediaProcessingStatus: {
        const mediaProcessingData = (
          props.notification as Notification<MediaProcessingStatusNotificationData>
        ).data;
        const isVideoMediaType =
          mediaProcessingData?.type === MediaProcessType.VideoMessage ||
          mediaProcessingData?.type === MediaProcessType.Video;
        setNotificationCardData({
          isValidNotification: true,
          message: getMediaProcessingStatusMessage(mediaProcessingData),
          icon: <FileUploadIcon className="notification-card-avatar-badge-icon" />,
          avatarName: authContext.state.session?.currentUser.name || '',
          avatarPicture: authContext.state.session?.currentUser.picture,
          wasRead: props.notification.wasRead === true ? true : false,
          imageUrl: isVideoMediaType
            ? mediaProcessingData?.thumbnailUrl
            : (mediaProcessingData?.mediaUrl ?? mediaProcessingData?.thumbnailUrl),
          videoUrl: isVideoMediaType ? mediaProcessingData?.mediaUrl : undefined,
          date: (props.notification as Notification<MediaProcessingStatusNotificationData>)
            .createdAt,
        });
        break;
      }
      case NotificationType.MediaEmpty:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.media_empty, {
            name:
              (props.notification as Notification<MediaEmptyNotificationData>).data?.userName || '',
          }),
          icon: <PhotoCameraIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<MediaEmptyNotificationData>).data?.userName || '',
          avatarPicture: (props.notification as Notification<MediaEmptyNotificationData>).data
            ?.userPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<MediaEmptyNotificationData>).createdAt,
        });
        break;
      case NotificationType.PaintingSent:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.painting_sent, {
            name:
              (props.notification as Notification<DrawingSentNotificationData>).data
                ?.fromUserName || '',
          }),
          icon: <ModeEditIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<DrawingSentNotificationData>).data?.fromUserName ||
            '',
          avatarPicture: (props.notification as Notification<DrawingSentNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          imageUrl: (props.notification as Notification<DrawingSentNotificationData>).data
            ?.mediaUrl,
          date: (props.notification as Notification<DrawingSentNotificationData>).createdAt,
        });
        break;
      case NotificationType.PaintingShared:
        setNotificationCardData({
          isValidNotification: true,
          message: t(translations.notification_card.painting_shared, {
            name:
              (props.notification as Notification<DrawingSharedNotificationData>).data
                ?.fromUserName || '',
          }),
          icon: <ModeEditIcon className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<DrawingSharedNotificationData>).data
              ?.fromUserName || '',
          avatarPicture: (props.notification as Notification<DrawingSharedNotificationData>).data
            ?.fromUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          imageUrl: (props.notification as Notification<DrawingSharedNotificationData>).data
            ?.mediaUrl,
          date: (props.notification as Notification<DrawingSharedNotificationData>).createdAt,
        });
        break;
      case NotificationType.NewOccurrence:
        setNotificationCardData({
          isValidNotification: true,
          message: t(
            translations.notification_card[
              (props.notification as Notification<OccurrenceNotificationData>).data?.targetUserName
                ? 'new_occurrence_patient'
                : 'new_occurrence'
            ],
            {
              name:
                (props.notification as Notification<OccurrenceNotificationData>).data
                  ?.occurrenceName || '',
              patient: (props.notification as Notification<OccurrenceNotificationData>).data
                ?.targetUserName,
            }
          ),
          icon: <WarningRounded className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<OccurrenceNotificationData>).data?.targetUserName ||
            '',
          avatarPicture: (props.notification as Notification<OccurrenceNotificationData>).data
            ?.targetUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<OccurrenceNotificationData>).createdAt,
          occurrenceId: (props.notification as Notification<OccurrenceNotificationData>).data
            ?.occurrenceId,
          occurrenceEntityId: (
            props.notification as Notification<OccurrenceAssignedNotificationData>
          ).data?.entityId,
          occurrenceRegisteredAt: (
            props.notification as Notification<OccurrenceAssignedNotificationData>
          ).data?.occurrenceRegisteredAt,
        });
        break;
      case NotificationType.NewOccurrenceAssigned:
        setNotificationCardData({
          isValidNotification: true,
          message: t(
            translations.notification_card[
              (props.notification as Notification<OccurrenceNotificationData>).data?.targetUserName
                ? 'new_occurrence_assigned_patient'
                : 'new_occurrence_assigned'
            ],
            {
              name:
                (props.notification as Notification<OccurrenceAssignedNotificationData>).data
                  ?.occurrenceName || '',
              registered:
                (props.notification as Notification<OccurrenceAssignedNotificationData>).data
                  ?.registeredByUserName || '',
              patient: (props.notification as Notification<OccurrenceAssignedNotificationData>).data
                ?.targetUserName,
            }
          ),
          icon: <WarningRounded className="notification-card-avatar-badge-icon" />,
          avatarName:
            (props.notification as Notification<OccurrenceAssignedNotificationData>).data
              ?.targetUserName || '',
          avatarPicture: (props.notification as Notification<OccurrenceAssignedNotificationData>)
            .data?.targetUserPicture,
          wasRead: props.notification.wasRead === true ? true : false,
          date: (props.notification as Notification<OccurrenceAssignedNotificationData>).createdAt,
          occurrenceId: (props.notification as Notification<OccurrenceNotificationData>).data
            ?.occurrenceId,
          occurrenceEntityId: (
            props.notification as Notification<OccurrenceAssignedNotificationData>
          ).data?.entityId,
          occurrenceRegisteredAt: (
            props.notification as Notification<OccurrenceAssignedNotificationData>
          ).data?.occurrenceRegisteredAt,
        });
        break;
      default:
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.notification]);

  // Get media processing status message
  const getMediaProcessingStatusMessage = (
    notificationData?: MediaProcessingStatusNotificationData
  ) => {
    // Set the message
    let message: string = '';
    switch (notificationData?.type) {
      case MediaProcessType.VideoMessage:
        message =
          notificationData.status === ProcessStatus.Done ||
          notificationData.status === ProcessStatus.Failed
            ? t(
                translations.notification_card.media_processing_status.video_message[
                  notificationData.status
                ],
                {
                  name: notificationData.targetName,
                }
              )
            : '';
        break;
      case MediaProcessType.Video:
        message =
          notificationData.status === ProcessStatus.Done ||
          notificationData.status === ProcessStatus.Failed
            ? notificationData.targetName
              ? t(
                  translations.notification_card.media_processing_status.video_shared[
                    notificationData.status
                  ],
                  {
                    name: notificationData.targetName,
                  }
                )
              : t(
                  translations.notification_card.media_processing_status.video_saved[
                    notificationData.status
                  ]
                )
            : '';
        break;
      case MediaProcessType.Photo:
        message =
          notificationData.status === ProcessStatus.Done ||
          notificationData.status === ProcessStatus.Failed
            ? notificationData.targetName
              ? t(
                  translations.notification_card.media_processing_status.photo_shared[
                    notificationData.status
                  ],
                  {
                    name: notificationData.targetName,
                  }
                )
              : t(
                  translations.notification_card.media_processing_status.photo_saved[
                    notificationData.status
                  ]
                )
            : '';
        break;
      case MediaProcessType.UserPicture:
        message =
          notificationData.status === ProcessStatus.Done ||
          notificationData.status === ProcessStatus.Failed
            ? t(
                translations.notification_card.media_processing_status.photo_saved[
                  notificationData.status
                ]
              )
            : '';
        break;
      default:
        message =
          notificationData?.status === ProcessStatus.Done ||
          notificationData?.status === ProcessStatus.Failed
            ? t(
                translations.notification_card.media_processing_status.unknown[
                  notificationData.status
                ]
              )
            : '';
        break;
    }

    // Return the message
    return message;
  };

  // Get relationship invite message
  const getRelationshipInviteMessage = (notificationData?: RelationshipInviteNotificationData) => {
    if (notificationData?.sentByEntityName) {
      return t(translations.notification_card.relationship_invite.message_sender, {
        sender: notificationData?.sentByEntityName || '',
        target: notificationData?.targetUserName || '',
      });
    } else if (
      notificationData?.sentByUserName &&
      notificationData.targetUserId === notificationData.sentByUserId
    ) {
      return t(translations.notification_card.relationship_invite.message_sender_same_as_target, {
        sender: notificationData?.sentByUserName || '',
      });
    } else if (notificationData?.sentByUserName) {
      return t(translations.notification_card.relationship_invite.message_sender, {
        sender: notificationData?.sentByUserName || '',
        target: notificationData?.targetUserName || '',
      });
    } else {
      return t(translations.notification_card.relationship_invite.message_sender_unknown, {
        target: notificationData?.targetUserName || '',
      });
    }
  };

  // Get affiliation invite message
  const getAffiliationInviteMessage = (notificationData?: AffiliationInviteNotificationData) => {
    if (notificationData?.sentByUserName) {
      return t(translations.notification_card.affiliation_invite.message_sender, {
        sender: notificationData?.sentByUserName || '',
        target: notificationData?.targetEntityName || '',
      });
    } else if (
      notificationData?.sentByEntityName &&
      notificationData.targetEntityId === notificationData.sentByEntityId
    ) {
      return t(translations.notification_card.affiliation_invite.message_sender_same_as_target, {
        sender: notificationData?.sentByEntityName || '',
      });
    } else if (notificationData?.sentByEntityName) {
      return t(translations.notification_card.affiliation_invite.message_sender, {
        sender: notificationData?.sentByEntityName || '',
        target: notificationData?.targetEntityName || '',
      });
    } else {
      return t(translations.notification_card.affiliation_invite.message_sender_unknown, {
        target: notificationData?.targetEntityName || '',
      });
    }
  };

  // Get relationship end message
  const getRelationshipEndMessage = (notificationData?: RelationshipEndNotificationData) => {
    if (notificationData) {
      if (notificationData.endedWithUserId === authContext.state.session?.id) {
        return t(translations.notification_card.relationship_end.message_myself, {
          name: notificationData.endedByUserName,
          brand: getBrandName(appContext.state.configuration.product),
        });
      } else {
        return t(translations.notification_card.relationship_end.message_other, {
          name: notificationData.endedByUserName,
          target: notificationData.endedWithUserName,
          brand: getBrandName(appContext.state.configuration.product),
        });
      }
    } else {
      return '';
    }
  };

  // Get relationship role change message
  const getRelationshipRoleChangeMessage = (
    notificationData?: RelationshipRoleChangeNotificationData
  ) => {
    if (notificationData) {
      if (notificationData.changeMadeByUserId) {
        // Senior made the change
        if (notificationData.relationshipWithSeniorId === notificationData.changeMadeByUserId) {
          if (notificationData.newRoleUserId === authContext.state.session?.id) {
            return t(
              translations.notification_card.relationship_role_change.message_senior_myself,
              {
                role: t(translations.roles[notificationData.newRole]),
                name: notificationData.changeMadeByUserName,
                brand: getBrandName(appContext.state.configuration.product),
              }
            );
          } else {
            return t(translations.notification_card.relationship_role_change.message_senior_other, {
              role: t(translations.roles[notificationData.newRole]),
              name: notificationData.changeMadeByUserName,
              target: notificationData.newRoleUserName,
              brand: getBrandName(appContext.state.configuration.product),
            });
          }
        } else {
          // Another person made the change
          if (notificationData.newRoleUserId === authContext.state.session?.id) {
            return t(
              translations.notification_card.relationship_role_change.message_caregiver_myself,
              {
                role: t(translations.roles[notificationData.newRole]),
                name: notificationData.changeMadeByUserName,
                senior: notificationData.relationshipWithSeniorName,
                brand: getBrandName(appContext.state.configuration.product),
              }
            );
          } else {
            return t(
              translations.notification_card.relationship_role_change.message_caregiver_other,
              {
                role: t(translations.roles[notificationData.newRole]),
                name: notificationData.changeMadeByUserName,
                senior: notificationData.relationshipWithSeniorName,
                target: notificationData.newRoleUserName,
                brand: getBrandName(appContext.state.configuration.product),
              }
            );
          }
        }
      } else {
        // Unknown person made the change
        if (notificationData.newRoleUserId === authContext.state.session?.id) {
          return t(translations.notification_card.relationship_role_change.message_unknown_myself, {
            role: t(translations.roles[notificationData.newRole]),
            senior: notificationData.relationshipWithSeniorName,
            brand: getBrandName(appContext.state.configuration.product),
          });
        } else {
          return t(translations.notification_card.relationship_role_change.message_unknown_other, {
            role: t(translations.roles[notificationData.newRole]),
            senior: notificationData.relationshipWithSeniorName,
            target: notificationData.newRoleUserName,
            brand: getBrandName(appContext.state.configuration.product),
          });
        }
      }
    } else {
      return '';
    }
  };

  // Get reminder response
  const getReminderResponse = (response?: ReminderResponseValue) => {
    switch (response) {
      case ReminderResponseValue.Negative:
        return t(translations.notification_card.reminder_response.responses.negative);
      case ReminderResponseValue.Positive:
        return t(translations.notification_card.reminder_response.responses.positive);
      case ReminderResponseValue.NotResponding:
        return t(translations.notification_card.reminder_response.responses.not_responding);
      default:
        return '';
    }
  };

  // Open/close the invite
  const handleOpenInvite = () => {
    // Set invite to open it
    if (notificationCardData.invite?.type === InviteType.Relationship) {
      officeContext.setInvite({
        identificationType: InviteIdentificationType.InviteId,
        identificationValue: notificationCardData.invite.id || '',
        inviteType: InviteType.Relationship,
      });
    } else if (notificationCardData.invite?.type === InviteType.Affiliation) {
      officeContext.setInvite({
        identificationType: InviteIdentificationType.InviteId,
        identificationValue: notificationCardData.invite.id || '',
        inviteType: InviteType.Affiliation,
      });
    }

    // Read notification
    if (!notificationCardData.wasRead) {
      handleUpdateNotificationWasRead(true);
    }
  };

  // Read/unread notification
  const handleUpdateNotificationWasRead = async (flag: boolean) => {
    try {
      // Log
      console.debug(`ℹ️ Notification ${props.notification.id} will update with wasRead: ${flag}.`);

      // Read/unread notification
      await NotificationsRepository.instance.updateStatusById(props.notification.id || '', flag);

      // Update notification
      setNotificationCardData({
        ...notificationCardData,
        wasRead: flag,
      });

      // New count
      let newCount: number = authContext.state.session?.unreadNotificationsCount
        ? authContext.state.session.unreadNotificationsCount
        : 0;
      if (flag) {
        newCount = newCount > 0 ? newCount - 1 : 0;
      } else {
        newCount += 1;
      }

      // Update unread notifications count
      authContext.updateSessionUnreadNotificationCount(newCount);
    } catch (error) {
      // Error
      console.debug(`🔴 Could not read/unread notification ${props.notification.id}.`, error);
    }
  };

  // Data charing row component
  const renderDataSharingRow = (title: string, checked: boolean) => (
    <Box className="notification-card-content-personal-data-sharing-preferences">
      <Typography variant="caption" component="span">
        {title}
      </Typography>
      <Switch disabled checked={checked} size="small" />
    </Box>
  );

  // Handle error on the image
  const handleErrorOnImage = () => {
    setErrorOnImage(true);
  };

  // Handle error on the video
  const handleErrorOnVideo = () => {
    setErrorOnVideo(true);
  };

  const renderData = () => {
    if (
      notificationCardData.personalDataSharingPreferences ||
      notificationCardData.invite ||
      notificationCardData.occurrenceId
    ) {
      return (
        <Box className="notification-card-data-container">
          {notificationCardData.invite && (
            <Box>
              <Button variant="contained" size="small" onClick={handleOpenInvite}>
                {t(translations.notification_card.open_invite)}
              </Button>
            </Box>
          )}
          {notificationCardData.personalDataSharingPreferences && (
            <Box className="notification-card-content-personal-data-sharing-preferences-container">
              <Typography
                variant="caption"
                component="span"
                className="notification-card-content-personal-data-sharing-preferences-title"
              >
                {t(
                  translations.notification_card.personal_data_sharing_preferences_change
                    .current_data_settings_title
                )}
              </Typography>
              {renderDataSharingRow(
                t(
                  translations.notification_card.personal_data_sharing_preferences_change
                    .can_access_activity_feed
                ),
                notificationCardData.personalDataSharingPreferences.canAccessActivityFeed
              )}
              {renderDataSharingRow(
                t(
                  translations.notification_card.personal_data_sharing_preferences_change
                    .can_access_applications
                ),
                notificationCardData.personalDataSharingPreferences.canAccessApplications
              )}
              {renderDataSharingRow(
                t(
                  translations.notification_card.personal_data_sharing_preferences_change
                    .can_access_contacts
                ),
                notificationCardData.personalDataSharingPreferences.canAccessContacts
              )}
              {renderDataSharingRow(
                t(
                  translations.notification_card.personal_data_sharing_preferences_change
                    .can_access_multimedia
                ),
                notificationCardData.personalDataSharingPreferences.canAccessMultimedia
              )}
              {renderDataSharingRow(
                t(
                  translations.notification_card.personal_data_sharing_preferences_change
                    .can_access_profile_data
                ),
                notificationCardData.personalDataSharingPreferences.canAccessProfileData
              )}
              {renderDataSharingRow(
                t(
                  translations.notification_card.personal_data_sharing_preferences_change
                    .can_access_reminders
                ),
                notificationCardData.personalDataSharingPreferences.canAccessReminders
              )}
            </Box>
          )}
          {notificationCardData.occurrenceId && notificationCardData.occurrenceEntityId && (
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  if (
                    authContext.state.session?.selectedAffiliation?.entity.id !==
                    notificationCardData.occurrenceEntityId
                  ) {
                    const newAffiliation = authContext.state.session?.affiliations.find(
                      (affiliation) =>
                        affiliation.entity.id === notificationCardData.occurrenceEntityId
                          ? affiliation
                          : undefined
                    );
                    // update selected affiliation
                    if (newAffiliation) authContext.updateSelectedAffiliation(newAffiliation);
                  }
                  history.push(
                    `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${notificationCardData.occurrenceEntityId}${ScreenPaths.ProfessionalCaregiver_Occurrences}?occurrenceId=${notificationCardData.occurrenceId}&occurrenceDate=${moment(notificationCardData.occurrenceRegisteredAt || notificationCardData.date).format('YYYY-MM-DD')}`
                  );
                  // Read notification
                  if (!notificationCardData.wasRead) handleUpdateNotificationWasRead(true);
                }}
              >
                {t(translations.notification_card.open_occurrences)}
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      {notificationCardData.isValidNotification && (
        <Box
          className="notification-card-container"
          sx={{
            backgroundColor: moment(notificationCardData.date).isSame(moment(), 'day')
              ? 'var(--dcm-bgseniormenu)'
              : 'var(--dcm-white-base',
          }}
        >
          <Box className="notification-card">
            <Box className="notification-card-avatar-container">
              <Tooltip title={notificationCardData.avatarName}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                  badgeContent={
                    <Avatar
                      className="notification-card-avatar-badge"
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        boxShadow:
                          'rgb(0 0 0 / 20%) 0px 2px 1px -1px, rgb(0 0 0 / 14%) 0px 1px 1px 0px, rgb(0 0 0 / 12%) 0px 1px 3px 0px',
                      }}
                    >
                      {notificationCardData.icon}
                    </Avatar>
                  }
                >
                  <Avatar
                    className="notification-card-avatar"
                    alt={notificationCardData.avatarName}
                    src={notificationCardData.avatarPicture}
                    variant="circular"
                  >
                    <PersonIcon className="notification-card-avatar-icon" />
                  </Avatar>
                </Badge>
              </Tooltip>
            </Box>
            <Box className="notification-card-content-container">
              <Box className="notification-card-content-text">
                <Box className="notification-card-content-message">
                  <Typography variant="body2" component="span">
                    {notificationCardData.message}
                  </Typography>
                </Box>
                <Box className="notification-card-content-footer">
                  <Tooltip
                    title={
                      notificationCardData.date
                        ? moment(notificationCardData.date).format('LLLL')
                        : ''
                    }
                  >
                    <Box className="notification-card-content-footer-slot">
                      <Typography variant="caption" color="text.secondary">
                        {moment(notificationCardData.date).fromNow() || ''}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
              {renderData()}
            </Box>

            <Box className="notification-card-avatar-read-container">
              <Tooltip
                title={
                  notificationCardData.wasRead
                    ? t(translations.notifications.check_unread)
                    : t(translations.notifications.check_read)
                }
              >
                <Radio
                  checked={!notificationCardData.wasRead}
                  onClick={() => handleUpdateNotificationWasRead(!notificationCardData.wasRead)}
                  color="info"
                  checkedIcon={<LensIcon />}
                />
              </Tooltip>
            </Box>
          </Box>
          {notificationCardData.imageUrl && !notificationCardData.videoUrl && !errorOnImage && (
            <Box className="notification-card-content-attachment-container">
              <Box className="notification-card-content-attachment-left"></Box>
              <Box className="notification-card-content-attachment">
                <img src={notificationCardData.imageUrl} onError={handleErrorOnImage} alt="" />
              </Box>

              <Box className="notification-card-content-attachment-right"></Box>
            </Box>
          )}
          {notificationCardData.videoUrl && !errorOnVideo && (
            <Box className="notification-card-content-attachment-container">
              <Box className="notification-card-content-attachment-left"></Box>
              <Box className="notification-card-content-attachment">
                <video
                  className="notification-card-content-attachment"
                  onError={handleErrorOnVideo}
                  controls
                >
                  <source src={notificationCardData.videoUrl} />
                </video>
              </Box>

              <Box className="notification-card-content-attachment-right"></Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export const NotificationPendingCard = ({
  data,
}: {
  data: {
    pendingCareTask: EntityPendingTask;
    careTask: CareTask;
  }[];
}) => {
  const {t} = useTranslation();
  let history = useHistory();
  const location = useLocation();
  const {state: authState} = useAuth();
  const pendingDate = data[data.length - 1].pendingCareTask.createdAt;

  return (
    <Box
      className="notification-card-container"
      sx={{
        backgroundColor: moment(data[data.length - 1].pendingCareTask.createdAt).isSame(
          moment(),
          'day'
        )
          ? 'var(--dcm-bgseniormenu)'
          : 'var(--dcm-white-base',
      }}
    >
      <Box className="notification-card">
        <Box className="notification-card-icon-container">
          <MdOutlineTipsAndUpdates />
        </Box>
        <Box className="notification-card-content-container">
          <Box className="notification-card-content-text">
            <Box className="notification-card-content-message">
              <Typography variant="body2" component="span">
                <Trans values={{number: data.length}}>
                  {t(translations.office.pending_tasks.alert)}
                </Trans>
              </Typography>
            </Box>
            <Box className="notification-card-content-footer">
              <Tooltip title={moment(pendingDate).format('LLLL') || ''}>
                <Box className="notification-card-content-footer-slot">
                  <Typography variant="caption" color="text.secondary">
                    {moment(pendingDate).fromNow() || ''}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          </Box>
          <Box className="notification-card-data-container">
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                if (!authState.session?.selectedAffiliation) return;
                history.push(
                  `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.Entity_Planner_PendingTasks}`,
                  {from: location.pathname}
                );
              }}
            >
              {t(translations.office.pending_tasks.see_tasks)}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
